import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Modal,
  Table,
  Tag,
  Space,
  Input,
  message,
  Button,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import axios from "axios";
import { useAuth0 } from "../../../react-auth0-spa";
import config from "../../../api_config.json";
import { useAppContext } from "../../../context";

const query = `
  query fetchModules($where: ModuleWhereInput) {
    modules(where: $where, first: 10000) {
      edges {
        node {
          objectId
          name
          description
          icon {
            url
          }
          module_category_pointer {
            objectId
            name
            darker_color
          }
          price
          is_active
          is_main
          is_product_line
          sku
          una_tantum
        }
      }
    }
  }
`
  .replace(/\r?\n|\r/g, "")
  .replace(/\t/g, " ");

const AddModulesModal = ({
  visible,
  onCancel,
  onSuccess,
  orgId,
  existingModuleIds = [],
}) => {
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [, setSearchText] = useState("");
  const [, setSearchedColumn] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const wasVisibleRef = useRef(false);
  const { isProd } = useAppContext();
  const env = isProd ? "prod" : "dev";
  const { getTokenSilently } = useAuth0();
  const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;

  const fetchModules = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getTokenSilently();
      const response = await axios({
        method: "post",
        url: graphqlUrl,
        data: {
          query,
          variables: {
            where: {
              is_active: { equalTo: true }
            },
          },
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Filter out already existing modules and una_tantum modules
      const availableModules = response.data.modules.edges
        .filter(edge => !existingModuleIds.includes(edge.node.objectId) && !edge.node.una_tantum)
        .map((edge) => ({
          ...edge.node,
          key: edge.node.objectId,
          category: edge.node.module_category_pointer?.name || "",
          categoryColor: edge.node.module_category_pointer?.darker_color,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

      setModules(availableModules);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching modules:", error);
      message.error("Errore durante il recupero dei moduli");
      setLoading(false);
    }
  }, [existingModuleIds, getTokenSilently, graphqlUrl]);

  useEffect(() => {
    const isOpening = visible && !wasVisibleRef.current;

    if (isOpening) {
      fetchModules();
      setSelectedRowKeys([]);
    }

    wasVisibleRef.current = visible;
  }, [visible, fetchModules]);

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ width: 188, marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : "",
  });

  const handleSubmit = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning("Seleziona almeno un modulo");
      return;
    }

    setSubmitting(true);
    try {
      const token = await getTokenSilently();
      const createMutation = `
        mutation CreateOrgModule($input: CreateOrg_moduleFieldsInput!) {
          createOrg_module(input: { fields: $input }) {
            org_module {
              objectId
            }
          }
        }
      `.replace(/\r?\n|\r/g, "").replace(/\t/g, " ");

      // Create modules one by one
      const promises = selectedRowKeys.map(moduleId => {
        // Find the module to get its SKU
        const module = modules.find(m => m.objectId === moduleId);
        const input = {
          org_pointer: { link: orgId },
          module_pointer: { link: moduleId },
          enabled: true,
          enabled_by: "manual",
          sku: module?.sku || "", // Add the sku field
        };

        return axios({
          method: "post",
          url: graphqlUrl,
          data: {
            query: createMutation,
            variables: {
              input,
            },
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      });

      await Promise.all(promises);

      message.success(`${selectedRowKeys.length} moduli aggiunti con successo`);
      onSuccess();
    } catch (error) {
      console.error("Error creating modules:", error);
      message.error("Errore durante l'aggiunta dei moduli");
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      title: "Icon",
      dataIndex: "icon",
      key: "icon",
      render: (icon) =>
        icon?.url ? (
          <img
            src={icon.url}
            alt=""
            onError={(e) => {
              e.target.style.display = "none";
              e.target.parentNode.textContent = "-";
            }}
            style={{
              width: 32,
              height: 32,
              objectFit: "contain",
              verticalAlign: "middle",
            }}
          />
        ) : (
          "-"
        ),
    },
    {
      title: "Nome",
      dataIndex: "name",
      key: "name",
      ...getColumnSearchProps("name"),
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "SKU",
      dataIndex: "sku",
      key: "sku",
      width: 120,
      render: (sku) =>
        sku ? (
          <Tag 
            color="default" 
            style={{ 
              background: "#fafafa",
              maxWidth: "100px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "inline-block"
            }}
            title={sku}
          >
            {sku}
          </Tag>
        ) : (
          "-"
        ),
    },
    {
      title: "Prezzo",
      dataIndex: "price",
      key: "price",
      sorter: (a, b) => (a.price || 0) - (b.price || 0),
      render: (price) => (price ? `${price.toFixed(2)} €` : "-"),
    },
    {
      title: "Categoria",
      dataIndex: "category",
      key: "category",
      filters: [...new Set(modules.map((m) => m.category))].map((cat) => ({
        text: cat,
        value: cat,
      })),
      onFilter: (value, record) => record.category === value,
      render: (text, record) =>
        text ? (
          <Tag color={record.categoryColor || "default"}>{text}</Tag>
        ) : (
          "-"
        ),
    },
    {
      title: "Tipologia",
      dataIndex: "is_product_line",
      key: "is_product_line",
      filters: [
        { text: "Linea di Prodotto", value: true },
        { text: "Modulo", value: false },
      ],
      onFilter: (value, record) => record.is_product_line === value,
      render: (isProductLine) => (
        <Tag color={isProductLine ? "blue" : "green"}>
          {isProductLine ? "Linea di Prodotto" : "Modulo"}
        </Tag>
      ),
    },
    {
      title: "Principale",
      dataIndex: "is_main",
      key: "is_main",
      filters: [
        { text: "Principale", value: true },
        { text: "Aggiuntivo", value: false },
      ],
      onFilter: (value, record) => record.is_main === value,
      render: (isMain, record) =>
        record.is_product_line ? (
          "-"
        ) : (
          <Tag color={isMain ? "purple" : "orange"}>
            {isMain ? "Principale" : "Aggiuntivo"}
          </Tag>
        ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys) => {
      setSelectedRowKeys(selectedRowKeys);
    },
  };

  return (
    <Modal
      title="Aggiungi Moduli"
      open={visible}
      onOk={handleSubmit}
      onCancel={onCancel}
      width={1200}
      confirmLoading={submitting}
      okText="Aggiungi Selezionati"
      cancelText="Annulla"
      okButtonProps={{
        disabled: selectedRowKeys.length === 0,
      }}
    >
      <p style={{ marginBottom: 16 }}>
        Seleziona i moduli da aggiungere a questa organizzazione. 
        I moduli selezionati saranno abilitati manualmente.
      </p>
      <Table
        rowSelection={rowSelection}
        columns={columns}
        dataSource={modules}
        loading={loading}
        size="small"
        scroll={{ y: 400 }}
        pagination={false}
      />
    </Modal>
  );
};

export default AddModulesModal;
